import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  asignarRotativo,
  desactivarRotativoAsignacion,
  getRotativosAgentes,
  getRotativosAsignaciones,
  getRotativosTurnos,
  RotativoAgente,
  RotativoAsignacion,
  RotativoTurno,
} from '../services/rotativos.service';
import { getErrorMessage } from '../utils/errorHandler';

const toIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const AsignacionRotativosPage: React.FC = () => {
  const [agentes, setAgentes] = useState<RotativoAgente[]>([]);
  const [turnos, setTurnos] = useState<RotativoTurno[]>([]);
  const [asignaciones, setAsignaciones] = useState<RotativoAsignacion[]>([]);
  const [selectedAgentes, setSelectedAgentes] = useState<string[]>([]);
  const [selectedTurno, setSelectedTurno] = useState('');
  const [fechaInicio, setFechaInicio] = useState<string>(() => toIsoDate(new Date()));
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [filtroAgenteId, setFiltroAgenteId] = useState('');
  const [filtroTurnoId, setFiltroTurnoId] = useState('');
  const [vigenciaFiltro, setVigenciaFiltro] = useState<'TODAS' | 'VIGENTES' | 'FINALIZADAS'>('TODAS');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [agentesResp, turnosResp, asignacionesResp] = await Promise.all([
        getRotativosAgentes(),
        getRotativosTurnos(),
        getRotativosAsignaciones(),
      ]);
      const agentesData = Array.isArray(agentesResp.data) ? agentesResp.data : [];
      const turnosData = Array.isArray(turnosResp.data) ? turnosResp.data : [];
      setAgentes(agentesData);
      setTurnos(turnosData);
      setAsignaciones(Array.isArray(asignacionesResp.data) ? asignacionesResp.data : []);
      setSelectedTurno((prev) => prev || turnosData[0]?._id || '');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const filteredAgentes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agentes;
    return agentes.filter((agente) => {
      const target = `${agente.nombre} ${agente.documento || ''} ${agente.idAgente || ''}`.toLowerCase();
      return target.includes(q);
    });
  }, [agentes, search]);

  const todayIso = toIsoDate(new Date());

  const filteredAsignaciones = useMemo(() => {
    return asignaciones.filter((asignacion) => {
      const agenteId =
        typeof asignacion.agenteId === 'string' ? asignacion.agenteId : asignacion.agenteId._id;
      const turnoId =
        typeof asignacion.turnoId === 'string' ? asignacion.turnoId : asignacion.turnoId._id;

      if (filtroAgenteId && agenteId !== filtroAgenteId) return false;
      if (filtroTurnoId && turnoId !== filtroTurnoId) return false;

      const fechaFin = asignacion.fechaFin || '';
      const vigente = !fechaFin || fechaFin >= todayIso;
      if (vigenciaFiltro === 'VIGENTES' && !vigente) return false;
      if (vigenciaFiltro === 'FINALIZADAS' && vigente) return false;

      return true;
    });
  }, [asignaciones, filtroAgenteId, filtroTurnoId, vigenciaFiltro, todayIso]);

  const toggleAgente = (id: string) => {
    setSelectedAgentes((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const onAsignar = async () => {
    if (!selectedTurno) {
      setError('Seleccione un turno rotativo');
      return;
    }
    if (!fechaInicio) {
      setError('Seleccione fecha de inicio');
      return;
    }
    if (selectedAgentes.length === 0) {
      setError('Seleccione al menos un agente');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const result = await asignarRotativo({
        turnoId: selectedTurno,
        fechaInicio,
        agenteIds: selectedAgentes,
      });
      setSelectedAgentes([]);
      setSuccess(
        `Asignacion completada. Creadas: ${result.data.creadas}, cerradas: ${result.data.cerradas}, omitidas: ${result.data.omitidas}.`,
      );
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const onDesactivar = async (id: string) => {
    setProcessingId(id);
    setError('');
    setSuccess('');
    try {
      await desactivarRotativoAsignacion(id);
      setSuccess('Asignacion desactivada correctamente');
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setProcessingId(null);
    }
  };

  const getAgenteNombre = (agente: RotativoAsignacion['agenteId']): string =>
    typeof agente === 'string' ? agente : agente.nombre;

  const getAgenteDni = (agente: RotativoAsignacion['agenteId']): string =>
    typeof agente === 'string' ? '-' : agente.documento || '-';

  const getTurnoNombre = (turno: RotativoAsignacion['turnoId']): string =>
    typeof turno === 'string' ? turno : turno.nombre;

  return (
    <>
      <h1 className="page-title">Turnos - Asignacion Rotativos</h1>

      <div className="card">
        <h2 className="card-title">Asignar turno rotativo</h2>
        {error && <div className="error-msg">{error}</div>}
        {success && <div className="success-msg">{success}</div>}

        <div className="form-grid">
          <div className="form-group">
            <label>Turno rotativo</label>
            <select value={selectedTurno} onChange={(e) => setSelectedTurno(e.target.value)}>
              <option value="">Seleccionar...</option>
              {turnos.map((turno) => (
                <option key={turno._id} value={turno._id}>
                  {turno.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Fecha inicio</label>
            <input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
          </div>

          <div className="form-group">
            <label>Buscar agente</label>
            <input
              placeholder="Nombre, DNI o ID agente"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="table-wrapper" style={{ marginBottom: '12px' }}>
          <table>
            <thead>
              <tr>
                <th>Sel</th>
                <th>Nombre</th>
                <th>DNI</th>
                <th>ID_Agente</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgentes.map((agente) => (
                <tr key={agente._id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedAgentes.includes(agente._id)}
                      onChange={() => toggleAgente(agente._id)}
                    />
                  </td>
                  <td>{agente.nombre}</td>
                  <td>{agente.documento || '-'}</td>
                  <td>{agente.idAgente || '-'}</td>
                </tr>
              ))}
              {filteredAgentes.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center' }}>
                    No hay colaboradores para mostrar
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <button className="btn btn-primary" type="button" disabled={saving || loading} onClick={onAsignar}>
          {saving ? 'Asignando...' : 'Asignar rotativo'}
        </button>
      </div>

      <div className="card">
        <h2 className="card-title">Asignaciones rotativas vigentes</h2>
        <div className="filters-grid" style={{ marginBottom: '12px' }}>
          <div className="form-group">
            <label>Filtrar por agente</label>
            <select value={filtroAgenteId} onChange={(e) => setFiltroAgenteId(e.target.value)}>
              <option value="">Todos</option>
              {agentes.map((agente) => (
                <option key={agente._id} value={agente._id}>
                  {agente.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Filtrar por turno</label>
            <select value={filtroTurnoId} onChange={(e) => setFiltroTurnoId(e.target.value)}>
              <option value="">Todos</option>
              {turnos.map((turno) => (
                <option key={turno._id} value={turno._id}>
                  {turno.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Vigencia</label>
            <select value={vigenciaFiltro} onChange={(e) => setVigenciaFiltro(e.target.value as 'TODAS' | 'VIGENTES' | 'FINALIZADAS')}>
              <option value="TODAS">Todas</option>
              <option value="VIGENTES">Vigentes</option>
              <option value="FINALIZADAS">Finalizadas</option>
            </select>
          </div>
        </div>
        {loading ? (
          <div className="loading">Cargando...</div>
        ) : filteredAsignaciones.length === 0 ? (
          <div className="empty-state">No hay asignaciones registradas.</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Agente</th>
                  <th>DNI</th>
                  <th>Turno</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredAsignaciones.map((asignacion) => (
                  <tr key={asignacion._id}>
                    <td>{getAgenteNombre(asignacion.agenteId)}</td>
                    <td>{getAgenteDni(asignacion.agenteId)}</td>
                    <td>{getTurnoNombre(asignacion.turnoId)}</td>
                    <td>{asignacion.fechaInicio}</td>
                    <td>{asignacion.fechaFin || '-'}</td>
                    <td>
                      <span className={`badge ${asignacion.activo ? 'badge-success' : 'badge-secondary'}`}>
                        {asignacion.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline btn-sm"
                        type="button"
                        disabled={!asignacion.activo || processingId === asignacion._id}
                        onClick={() => onDesactivar(asignacion._id)}
                      >
                        {processingId === asignacion._id ? 'Procesando...' : 'Desactivar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AsignacionRotativosPage;
